import React from "react";

import type { MultiSelectOption } from "@proda-ai/ui-kit";
import { Button, MultiSelect } from "@proda-ai/ui-kit";

import Typography from "@/ui/atoms/Typography";
import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import Loader from "@/ui/atoms/Loader";

import type { GridApi } from "@/ui/molecules/Grid";
import { useLazyGridModuleExcelExport } from "@/ui/molecules/Grid";
import GridLoader from "@/ui/molecules/GridLoader";
import TopBarOverview from "@/ui/molecules/TopBarOverview";

import GridChartOfAccountsMapping from "@/ui/organisms/GridChartOfAccountsMapping/GridChartOfAccountsMapping";
import type { TOptionWithType } from "@/ui/organisms/typesGrids";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import LayoutFillParentWithFooter from "@/ui/layouts/LayoutFillParentWithFooter";
import LayoutWithTopBar from "@/ui/layouts/LayoutWithTopBar";
import LayoutFillParentOverlay from "@/ui/layouts/LayoutFillParentOverlay";

import type { TCoAMappingsAccountDTO } from "@/store/sliceCustomChartOfAccountsMappings/TCoAMappingsAccountDTO";
import type {
    TAccountMappingTableRow,
    AssetType,
    TAccountMappingTableDTO,
} from "@/store/sliceCustomChartOfAccountsMappings/TAccountMappingTableDTO";

import { stringUnionToArray } from "@/utils/types";
import { assertNever } from "@/utils/guards";

type TAlteredCustomAccount = {
    accountCode: string;
    mappedAccountCode: {
        from: string | null;
        to: string | null;
    };
};

const assetTypeLabel = (assetType: AssetType): string => {
    switch (assetType) {
        case "multifamily":
            return "Multi Family";
        case "mobile_home":
            return "Mobile Home";
        case "office":
            return "Office";
        case "retail":
            return "Retail";
        case "industrial":
            return "Industrial";
        case "mixed_use":
            return "Mixed Use";
        case "self_storage":
            return "Self Storage";
        case "hospitality":
            return "Hospitality";
        case "healthcare":
            return "Healthcare";
        default:
            assertNever(assetType);
    }
};

type AssetTypesMultiSelectProps = {
    availableAssetTypes: AssetType[];
    selectedValues: AssetType[];
    setSelectedValues: (values: AssetType[]) => void;
};
const AssetTypesMultiSelect = ({
    availableAssetTypes,
    selectedValues,
    setSelectedValues,
}: AssetTypesMultiSelectProps) => {
    const options: MultiSelectOption<AssetType>[] = [...availableAssetTypes];
    return (
        <MultiSelect
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            options={options}
            getOptionId={value => value}
            getOptionLabel={assetTypeLabel}
            props={{
                MultiSelectInput: {
                    placeholder: "Select ...",
                },
            }}
            selectedValuesString={
                selectedValues.length === 0
                    ? "None selected"
                    : selectedValues.length === 1
                      ? assetTypeLabel(selectedValues[0] as AssetType)
                      : `${selectedValues.length} selected`
            }
        />
    );
};

export type MappingStatus = "mapped" | "unmapped";

export const allMappingStatuses: MappingStatus[] =
    stringUnionToArray<MappingStatus>()("mapped", "unmapped");

const mappingStatusLabel = (mappingStatus: MappingStatus): string => {
    switch (mappingStatus) {
        case "mapped":
            return "Mapped accounts";
        case "unmapped":
            return "Unmapped accounts";
        default:
            assertNever(mappingStatus);
    }
};

const mappingStatusOptions: MultiSelectOption<MappingStatus>[] = [
    ...allMappingStatuses,
];

type MappingStatusMultiSelectProps = {
    selectedValues: MappingStatus[];
    setSelectedValues: (values: MappingStatus[]) => void;
};
const MappingStatusMultiSelect = ({
    selectedValues,
    setSelectedValues,
}: MappingStatusMultiSelectProps) => {
    return (
        <MultiSelect
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            options={mappingStatusOptions}
            getOptionId={value => value}
            getOptionLabel={mappingStatusLabel}
            props={{
                MultiSelectInput: {
                    placeholder: "Select ...",
                },
            }}
            selectedValuesString={
                selectedValues.length === 0
                    ? "No accounts"
                    : selectedValues.length === 1
                      ? mappingStatusLabel(selectedValues[0] as MappingStatus)
                      : `All accounts`
            }
        />
    );
};

export type ViewChartsOfAccountsMappingsParams = {
    useState: ({ coaId }: { coaId: number }) => {
        allCoAs:
            | {
                  coaId: number;
                  name: string;
                  description: string;
              }[]
            | null;
        allCoAsRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        accountMappingTableStatus: "idle" | "pending" | "rejected" | "resolved";
        customAccountsData: TCoAMappingsAccountDTO[] | null;
        mappingTable: TAccountMappingTableDTO[] | null;
        isSaveCustomMappingsPending: boolean;
        isSaveCustomMappingsError: boolean;
        alteredCustomAccounts: TAlteredCustomAccount[];
        visibleAccountMappingTableData: TAccountMappingTableRow[] | null;
        isProdaUser: boolean;
        isCoaDraft: boolean;
        availableAssetTypes: AssetType[];
        selectedAssetTypes: AssetType[];
        selectedMappingStatuses: MappingStatus[];
    };
    useCases: () => {
        changeCustomAccountMapping: (options: {
            accountCode: string;
            newMappedAccountCode: string | null;
            mappingTable: TAccountMappingTableDTO[];
            customAccounts: TCoAMappingsAccountDTO[];
        }) => void;
        saveAlteredCustomAccounts: (options: { coaId: number }) => void;
        navigateToChartsOfAccountsEditDetails: (coaId: number) => void;
        selectAssetTypes: (values: AssetType[]) => void;
        selectMappingStatuses: (values: MappingStatus[]) => void;
    };
    ComponentConcurrentUsersIndicator: React.FC;
    ComponentConcurrentUsersBanner: React.FC;
    ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount: React.FC<{
        extractionId: number;
        resourceKey: "coa-remapping";
    }>;
};

export type ViewChartsOfAccountsMappingsProps = BoxProps & {
    coaId: number;
    onClickBackButton: (params: { coaId: number }) => void;
};

export const createViewChartsOfAccountsMappings = ({
    useState,
    useCases,
    ComponentConcurrentUsersIndicator = () => null,
    ComponentConcurrentUsersBanner = () => null,
    ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount = () => null,
}: ViewChartsOfAccountsMappingsParams) => {
    const ViewChartsOfAccountsMappings = ({
        coaId,
        onClickBackButton,
        ...props
    }: ViewChartsOfAccountsMappingsProps) => {
        const { ExcelExportModule, isExcelModuleLoading, excelModuleError } =
            useLazyGridModuleExcelExport();

        const {
            allCoAs,
            allCoAsRequestStatus,
            accountMappingTableStatus,
            customAccountsData,
            mappingTable,
            isSaveCustomMappingsPending,
            isSaveCustomMappingsError,
            alteredCustomAccounts,
            visibleAccountMappingTableData,
            isProdaUser,
            isCoaDraft,
            availableAssetTypes,
            selectedAssetTypes,
            selectedMappingStatuses,
        } = useState({ coaId });

        const {
            changeCustomAccountMapping,
            saveAlteredCustomAccounts,
            navigateToChartsOfAccountsEditDetails,
            selectAssetTypes,
            selectMappingStatuses,
        } = useCases();

        const [gridApi, setGridApi] = React.useState<GridApi | null>(null);

        const coa = allCoAs?.find(coa => coa.coaId === coaId);
        const noMatch = Boolean(allCoAs && allCoAs.length > 0 && !coa);

        const isLoading =
            accountMappingTableStatus === "pending" ||
            accountMappingTableStatus === "idle" ||
            isExcelModuleLoading;

        const hasError =
            allCoAsRequestStatus === "rejected" ||
            accountMappingTableStatus === "rejected" ||
            isSaveCustomMappingsError ||
            Boolean(excelModuleError) ||
            noMatch;

        const isSaving = isSaveCustomMappingsPending;

        const customAccounts = React.useMemo(
            () => customAccountsData || [],
            [customAccountsData],
        );
        const availableAccountsOptions: TOptionWithType[] = React.useMemo(
            () =>
                customAccounts.map(account => ({
                    label: account.account,
                    value: account.code,
                    type: account.type,
                })),
            [customAccounts],
        );

        const handleChangeAccountMapping = ({
            accountCode,
            mappedAccountCode,
        }: {
            accountCode: string;
            mappedAccountCode: {
                from: string | null;
                to: string | null;
            };
        }) => {
            changeCustomAccountMapping({
                accountCode,
                newMappedAccountCode: mappedAccountCode.to,
                mappingTable: mappingTable ?? [],
                customAccounts: customAccountsData ?? [],
            });
        };

        const goBackIfNotProdaUser = React.useCallback(() => {
            if (isProdaUser) return;
            onClickBackButton({ coaId });
        }, [isProdaUser, onClickBackButton, coaId]);
        React.useEffect(() => {
            goBackIfNotProdaUser();
        }, [goBackIfNotProdaUser]);

        return (
            <>
                <ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount
                    extractionId={coaId}
                    resourceKey={"coa-remapping"}
                />{" "}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        height: "100%",
                        ...(props.sx || {}),
                    }}
                    {...props}
                >
                    <TopBarOverview
                        title={coa?.name}
                        backButtonLabel="Back to Chart of Account view"
                        rightContent={<ComponentConcurrentUsersIndicator />}
                        onBackButtonClick={() => onClickBackButton({ coaId })}
                    />
                    <LayoutWithTopBar
                        displayVerticalDivider={false}
                        displayHorizontalDivider={false}
                        leftContent={
                            <>
                                {isProdaUser && (
                                    <>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                whiteSpace: "nowrap",
                                                marginRight: "16px",
                                            }}
                                        >
                                            Asset Types:{" "}
                                        </Typography>
                                        <AssetTypesMultiSelect
                                            availableAssetTypes={
                                                availableAssetTypes
                                            }
                                            selectedValues={selectedAssetTypes}
                                            setSelectedValues={selectAssetTypes}
                                        />
                                    </>
                                )}
                                <Typography
                                    variant="h4"
                                    sx={{
                                        whiteSpace: "nowrap",
                                        ...(isProdaUser
                                            ? { marginLeft: "16px" }
                                            : {}),
                                        marginRight: "16px",
                                    }}
                                >
                                    Filter Accounts:{" "}
                                </Typography>
                                <MappingStatusMultiSelect
                                    selectedValues={selectedMappingStatuses}
                                    setSelectedValues={selectMappingStatuses}
                                />
                            </>
                        }
                        rightContent={<></>}
                    >
                        <LayoutFillParentWithFooter
                            footerContent={
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "16px 24px",
                                        borderTop:
                                            "1px solid var(--proda-neutral-200)",
                                    }}
                                >
                                    {isCoaDraft ? (
                                        <Button
                                            pattern="secondary"
                                            isGhost={true}
                                            isInline={true}
                                            size={"large"}
                                            leadingIcon={"chevron-left"}
                                            onClick={() =>
                                                navigateToChartsOfAccountsEditDetails(
                                                    coaId,
                                                )
                                            }
                                        >
                                            Back to editing Details
                                        </Button>
                                    ) : (
                                        <Box></Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: "16px",
                                            padding: "16px 24px",
                                            borderTop:
                                                "1px solid var(--proda-neutral-200)",
                                        }}
                                    >
                                        <Button
                                            pattern="secondary"
                                            size={"medium"}
                                            disabled={!gridApi}
                                            onClick={() => {
                                                if (gridApi) {
                                                    gridApi.exportDataAsExcel({
                                                        sheetName:
                                                            coa?.name ??
                                                            "CoA Mappings",
                                                    });
                                                }
                                            }}
                                        >
                                            Export as Excel
                                        </Button>
                                        <Button
                                            pattern="danger"
                                            isGhost={true}
                                            size={"medium"}
                                            disabled={
                                                alteredCustomAccounts.length ===
                                                    0 ||
                                                isSaving ||
                                                isLoading ||
                                                hasError
                                            }
                                            onClick={() =>
                                                onClickBackButton({ coaId })
                                            }
                                        >
                                            Discard Changes
                                        </Button>
                                        <Button
                                            pattern="primary"
                                            size={"medium"}
                                            disabled={
                                                alteredCustomAccounts.length ===
                                                    0 ||
                                                isSaving ||
                                                isLoading ||
                                                hasError
                                            }
                                            onClick={() => {
                                                saveAlteredCustomAccounts({
                                                    coaId,
                                                });
                                            }}
                                        >
                                            {isSaving
                                                ? "Saving..."
                                                : "Save Changes"}
                                        </Button>
                                    </Box>
                                </Box>
                            }
                        >
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    position: "relative",
                                }}
                            >
                                <ComponentConcurrentUsersBanner />
                                {isLoading && <GridLoader />}
                                {isSaving && (
                                    <LayoutFillParentOverlay>
                                        <LayoutFillParentCentered>
                                            <Loader />
                                        </LayoutFillParentCentered>
                                    </LayoutFillParentOverlay>
                                )}
                                {hasError && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            padding: "0 24px",
                                        }}
                                    >
                                        <ErrorMessage>
                                            Failed to load chart of accounts.
                                            Please try again.
                                        </ErrorMessage>
                                    </Box>
                                )}

                                {!isLoading &&
                                    !hasError &&
                                    ExcelExportModule &&
                                    visibleAccountMappingTableData &&
                                    visibleAccountMappingTableData.length >
                                        0 && (
                                        <Box
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <GridChartOfAccountsMapping
                                                rowData={
                                                    visibleAccountMappingTableData
                                                }
                                                optionsAccounts={
                                                    availableAccountsOptions
                                                }
                                                alteredAccounts={
                                                    alteredCustomAccounts
                                                }
                                                onChangeAccountMapping={
                                                    handleChangeAccountMapping
                                                }
                                                modules={[ExcelExportModule]}
                                                onGridReady={params => {
                                                    setGridApi(params.api);
                                                }}
                                            />
                                        </Box>
                                    )}
                            </Box>
                        </LayoutFillParentWithFooter>
                    </LayoutWithTopBar>
                </Box>
            </>
        );
    };

    return ViewChartsOfAccountsMappings;
};
